// ============================================================
// Chess Arena — AI Controller (Minimax-based)
// ============================================================
// Controls the enemy side using a fixed-interval timer.
// Uses minimax search with alpha-beta pruning for move
// selection. Difficulty is configurable per level via
// depth, eval noise, blunder rate, and timing params.
// Retains bush stealth / last-known-position tracking.
// ============================================================

import {
  GridPos,
  PieceState,
  PieceType,
  Team,
  TerrainType,
} from '../core/types';
import {
  AIDifficultyConfig,
  BoardSnapshot,
  createSnapshot,
  DIFFICULTY_PRESETS,
  findBestMoves,
  SnapPiece,
} from './minimaxSearch';

// -----------------------------------------------------------------
// Callback interface — the AI doesn't own the board; it queries
// and commands through these hooks.
// -----------------------------------------------------------------

export interface AICallbacks {
  /** Return all living pieces for the given team. */
  getPieces: (team: Team) => PieceState[];
  /** Return valid target cells for a given piece. */
  getValidMoves: (piece: PieceState) => GridPos[];
  /** Get the piece (if any) at a position. */
  getPieceAt: (pos: GridPos) => PieceState | null;
  /** Execute a move for the given piece to the target cell. */
  movePiece: (pieceId: string, target: GridPos) => void;
  /** Check whether a cell is inside a bush. */
  isBush: (pos: GridPos) => boolean;
  /** Check whether tutorial has enemies frozen. */
  areEnemiesFrozen: () => boolean;
  /** Get the terrain grid for snapshot creation. */
  getTerrainGrid: () => (TerrainType | null)[][];
}

// -----------------------------------------------------------------
// AIController
// -----------------------------------------------------------------

export class AIController {
  private callbacks!: AICallbacks;
  private active = false;
  private difficulty!: AIDifficultyConfig;

  // Interval-based timing
  private moveTimer = 0;

  /**
   * Last-known-position map.
   * Key = player piece id, Value = last cell where the AI saw this piece.
   * Cleared when the piece is captured or leaves a bush.
   */
  private lastKnownPositions: Map<string, GridPos> = new Map();

  // ------------------------------------------------------------------
  // Lifecycle
  // ------------------------------------------------------------------

  init(
    callbacks: AICallbacks,
    difficulty: Partial<AIDifficultyConfig> = {},
  ): void {
    this.callbacks = callbacks;
    this.active = true;
    this.moveTimer = 0;
    this.difficulty = { ...DIFFICULTY_PRESETS.medium, ...difficulty };
    this.lastKnownPositions.clear();
  }

  reset(): void {
    this.active = false;
    this.moveTimer = 0;
    this.lastKnownPositions.clear();
  }

  // ------------------------------------------------------------------
  // Frame update — interval-based timing
  // ------------------------------------------------------------------

  update(deltaSec: number): void {
    if (!this.active) return;
    if (this.callbacks.areEnemiesFrozen()) return;

    this.moveTimer += deltaSec;
    if (this.moveTimer < this.difficulty.moveInterval) return;
    this.moveTimer = 0;

    this.updateVisibility();
    this.attemptMoves();
  }

  // ------------------------------------------------------------------
  // Move logic — snapshot board, run minimax, execute best moves
  // ------------------------------------------------------------------

  private attemptMoves(): void {
    const allEnemy = this.callbacks.getPieces('enemy');
    const readyPieces = allEnemy.filter(
      (p) => p.isAlive && !p.isMoving && !p.isFrozen && p.cooldownRemaining <= 0,
    );
    if (readyPieces.length === 0) return;

    // Build a snapshot of the current board for minimax
    const snap = this.buildSnapshot(readyPieces);

    // Find best moves via minimax search
    const bestMoves = findBestMoves(
      snap,
      'enemy',
      this.difficulty,
      this.difficulty.movesPerTick,
    );

    // Execute the chosen moves
    for (const move of bestMoves) {
      // Verify the piece is still ready (could have been captured mid-tick)
      const piece = readyPieces.find(p => p.id === move.pieceId);
      if (piece && piece.isAlive && !piece.isMoving) {
        this.callbacks.movePiece(move.pieceId, move.target);
      }
    }
  }

  /**
   * Build a BoardSnapshot from current game state.
   * Only includes pieces that are alive. For hidden player pieces
   * (in bushes), uses last-known position instead of real position.
   */
  private buildSnapshot(readyEnemyPieces: PieceState[]): BoardSnapshot {
    const enemyPieces = this.callbacks.getPieces('enemy')
      .filter(p => p.isAlive)
      .map(p => ({
        id: p.id,
        type: p.type,
        team: p.team as Team,
        pos: { ...p.pos },
      }));

    const playerPieces: SnapPiece[] = [];
    for (const p of this.callbacks.getPieces('player')) {
      if (!p.isAlive) continue;
      const inBush = this.callbacks.isBush(p.pos);
      if (inBush) {
        // Use last-known position if available, otherwise skip
        const lkp = this.lastKnownPositions.get(p.id);
        if (lkp) {
          playerPieces.push({
            id: p.id,
            type: p.type,
            team: p.team as Team,
            pos: { ...lkp },
          });
        }
        // If no LKP, the AI doesn't know about this piece at all
      } else {
        playerPieces.push({
          id: p.id,
          type: p.type,
          team: p.team as Team,
          pos: { ...p.pos },
        });
      }
    }

    const terrain = this.callbacks.getTerrainGrid();
    return createSnapshot([...enemyPieces, ...playerPieces], terrain);
  }

  // ------------------------------------------------------------------
  // Bush / stealth visibility tracking
  // ------------------------------------------------------------------

  private updateVisibility(): void {
    const playerPieces = this.callbacks.getPieces('player');

    for (const piece of playerPieces) {
      if (!piece.isAlive) {
        this.lastKnownPositions.delete(piece.id);
        continue;
      }

      const inBush = this.callbacks.isBush(piece.pos);
      if (!inBush) {
        this.lastKnownPositions.set(piece.id, { ...piece.pos });
      }
    }
  }

  // ------------------------------------------------------------------
  // Bush same-cell visibility (for MoveValidator integration)
  // ------------------------------------------------------------------

  canSeeFromBush(aiPiece: PieceState, targetPos: GridPos): boolean {
    if (!this.callbacks.isBush(aiPiece.pos)) return true;
    if (!this.callbacks.isBush(targetPos)) return true;
    return aiPiece.pos.col === targetPos.col && aiPiece.pos.row === targetPos.row;
  }
}
